import { useMemo } from 'react';

import { isReplayMode } from '../utils/replayToken';
import { isShareMode } from '../utils/shareToken';
import { useGlobalVariables } from './globalConfig/securedConfigHook';

type EnvConfig = {
  fabrics: {
    unifiedFabricUX: boolean;
    orchestration: {
      allowNewConnections: boolean;
      allowConnectionMonitoring: boolean;
    };
    crud: {
      showProviderDropdownForOrchestration: boolean;
    };
  };
  observability: {
    enabled: boolean;
  };
  sql: {
    pipelines: {
      allowSchedule: boolean;
      enableOrchestrationGems: boolean;
    };
    jobs: {
      allowCreate: boolean;
      allowSchedule: boolean;
    };
    allowFileUpload: boolean;
    simpleVersionControl: {
      enableProjectDeployment: boolean;
    };
  };
};

const defaultEnvConfig: EnvConfig = {
  fabrics: {
    unifiedFabricUX: false,
    orchestration: {
      allowNewConnections: true,
      allowConnectionMonitoring: false
    },
    crud: {
      showProviderDropdownForOrchestration: false
    }
  },
  observability: {
    enabled: true
  },
  sql: {
    pipelines: {
      allowSchedule: true,
      enableOrchestrationGems: true
    },
    jobs: {
      allowCreate: true,
      allowSchedule: true
    },
    allowFileUpload: true,
    simpleVersionControl: {
      enableProjectDeployment: true
    }
  }
};

// TODO: Move SAP config to envConfig
export const useEnvConfig = () => {
  const {
    isProphecyAutomateDisabled,
    showOnlyAirflowConnections,
    isSapEnabled,
    isUnifiedFabricEnabled,
    isConnectionMonitoringEnabled
  } = useGlobalVariables();
  const isShareOrReplayProject = isShareMode() || isReplayMode();

  const envConfig = useMemo(() => {
    const config = {
      ...defaultEnvConfig
    };

    if (isProphecyAutomateDisabled) {
      config.fabrics.orchestration.allowNewConnections = false;
      config.sql.pipelines.enableOrchestrationGems = false;
      config.sql.pipelines.allowSchedule = false;
      config.observability.enabled = false;
      config.sql.jobs.allowCreate = false;
      config.sql.jobs.allowSchedule = false;
      config.sql.allowFileUpload = false;
      config.sql.simpleVersionControl.enableProjectDeployment = false;
    }

    // allow job creation and new connection creation even when isProphecyAutomateDisabled is false
    if (showOnlyAirflowConnections) {
      config.fabrics.orchestration.allowNewConnections = true;
      config.sql.jobs.allowCreate = true;
    }

    if (isShareOrReplayProject) {
      config.fabrics.orchestration.allowNewConnections = false;
    }

    if (isUnifiedFabricEnabled) {
      config.fabrics.unifiedFabricUX = true;
    }

    if (isSapEnabled) {
      config.fabrics.crud.showProviderDropdownForOrchestration = false;
    }

    if (isConnectionMonitoringEnabled) {
      config.fabrics.orchestration.allowConnectionMonitoring = true;
    }

    return config;
  }, [
    isProphecyAutomateDisabled,
    showOnlyAirflowConnections,
    isShareOrReplayProject,
    isUnifiedFabricEnabled,
    isSapEnabled,
    isConnectionMonitoringEnabled
  ]);

  return envConfig;
};
